import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Ban, ShieldCheck } from "lucide-react";
import { Banner, Button, KeyValues, StatusDot, TextArea, useToast } from "@/shared/rd";
import { Actions, SectionCard } from "@/shared/stores/components/rd-local";

import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { capturePrivateSession, isPrivateSessionCurrent } from "@/lib/api/private-session";
import type { AdminTagManagerView } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import {
  ApiJsonClientError,
  apiJsonClient,
} from "@/lib/query/core/api-json-client";
import {
  storeTagManagerQueryKey,
  useSetTagManagerDisabledMutation,
} from "@/modules/stores/services/mutations/useSetTagManagerDisabledMutation";

type Props = {
  storeId: string;
  storefrontUrl: string;
};

/**
 * The kill switch for a store's Google Tag Manager container.
 *
 * A container executes whatever JavaScript the seller configured in it, on
 * every storefront route including checkout. This is the instrument for the
 * cases where that turns out to be a problem — a seller who pasted a container
 * someone handed them, a container serving malware, a container that simply
 * broke the store — without editing the seller's settings row behind their back
 * and without withdrawing the capability from every store at once.
 *
 * Rendered only for ADMIN / SUPER_ADMIN, mirroring the endpoint's own
 * ManagingAdminGuard. That is a UI courtesy, not the enforcement: the API
 * refuses anyone else regardless, and this card surfaces that refusal rather
 * than swallowing it into a toggle that looks like it worked.
 */
export function StorefrontTagManagerCard({ storeId, storefrontUrl }: Props) {
  const t = useTranslations();
  const toast = useToast();
  const lifetime = useRef(capturePrivateSession()).current;
  const { mutateAsync, isPending } = useSetTagManagerDisabledMutation();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  /** The switch that failed, for the banner's title and its Try again. */
  const [failed, setFailed] = useState<boolean | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: storeTagManagerQueryKey(storeId),
    queryFn: () =>
      apiJsonClient<AdminTagManagerView>(
        endpoints.admin.storeTagManager(storeId),
      ),
    enabled: storeId.length > 0,
  });

  const apply = async (disabled: boolean, undoing = false): Promise<void> => {
    setError(null);
    setFailed(null);
    try {
      const result = await mutateAsync({
        storeId,
        disabled,
        reason: disabled ? reason.trim() || null : null,
      });
      if (!isPrivateSessionCurrent(lifetime)) return;
      setReason("");
      // The write is durable either way; only the cache bust is best-effort.
      // Saying "disabled" flatly after a failed bust would be a lie at exactly
      // the moment somebody is pulling a container because it is doing harm.
      // The same endpoint flips it back, so the answer offers Undo.
      toast.show({
        tone: "neutral",
        message: result.revalidated
          ? t(keys.admin.storeTagManagerApplied)
          : t(keys.admin.storeTagManagerAppliedDelayed),
        ...(undoing ? {} : { actionLabel: t("admin.undo"), onAction: () => void apply(!disabled, true) }),
      });
    } catch (caught) {
      if (!isPrivateSessionCurrent(lifetime)) return;
      // Snap back: `data` is untouched because the mutation never resolved, so
      // the toggle re-renders from the server's last known state.
      setFailed(disabled);
      setError(
        caught instanceof ApiJsonClientError && caught.message
          ? caught.message
          : t(keys.errors.generic),
      );
    }
  };

  const disabled = data?.disabled ?? false;
  const safeModeUrl = `${storefrontUrl}${storefrontUrl.includes("?") ? "&" : "?"}safe=1`;

  const stateDot = disabled ? (
    <StatusDot tone="red">{t(keys.admin.storeTagManagerStateDisabled)}</StatusDot>
  ) : data?.containerId ? (
    <StatusDot tone="green">{t(keys.admin.storeTagManagerStateAllowed)}</StatusDot>
  ) : (
    <StatusDot tone="grey">{t(keys.admin.storeTagManagerStateNone)}</StatusDot>
  );

  const details = data?.containerId
    ? [
        {
          label: t(keys.admin.storeTagManagerContainer),
          value: (
            <span dir="ltr" style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
              {data.containerId}
            </span>
          ),
        },
        ...(data.setAt
          ? [
              {
                label: t(keys.admin.storeTagManagerSetBy),
                value: `${data.setByName ?? t(keys.admin.storeTagManagerSetByUnknown)} · ${formatDate(data.setAt)}`,
              },
            ]
          : []),
      ]
    : [];

  return (
    <SectionCard
      badge="hub"
      title={t(keys.admin.storeTagManagerTitle)}
      note={t(keys.admin.storeTagManagerDesc)}
      aside={!isLoading ? <span data-disabled={disabled ? "true" : "false"}>{stateDot}</span> : null}
    >
      {error && failed !== null ? (
        <Banner
          tone="error"
          title={t(failed ? "admin.storeTagManagerDisableFailedTitle" : "admin.storeTagManagerAllowFailedTitle")}
          actions={
            <Button size="sm" loading={isPending} loadingLabel={t("admin.busyTrying")} onClick={() => void apply(failed)}>
              {t(keys.shell.retry)}
            </Button>
          }
        >
          {error} {t("admin.nothingChanged")}
        </Banner>
      ) : null}

      {details.length ? <KeyValues rows={details} /> : null}

      {disabled ? (
        <p style={{ margin: 0, fontSize: 13, color: "var(--rd-text-secondary)" }}>
          {t(keys.admin.storeTagManagerDisabledBy, {
            name: data?.disabledByName ?? t(keys.admin.storeTagManagerSetByUnknown),
            date: data?.disabledAt ? formatDate(data.disabledAt) : "",
          })}
          {data?.disabledReason ? ` — ${data.disabledReason}` : ""}
        </p>
      ) : (
        <TextArea
          aria-label={t(keys.admin.storeTagManagerReasonPlaceholder)}
          name="tagManagerDisableReason"
          rows={2}
          value={reason}
          placeholder={t(keys.admin.storeTagManagerReasonPlaceholder)}
          onChange={(e) => setReason(e.target.value)}
        />
      )}


      <Actions>
        {/* The diagnostic rendering, next to the switch that needs it: it is how
            you confirm the container was what broke the storefront. */}
        <Button size="sm" icon={ShieldCheck} onClick={() => window.open(safeModeUrl, "_blank", "noopener")}>
          {t(keys.admin.storeTagManagerSafeMode)}
        </Button>
        <Button
          size="sm"
          variant={disabled ? "secondary" : "danger"}
          icon={disabled ? undefined : Ban}
          loading={isPending}
          loadingLabel={t("admin.busyApplying")}
          disabled={isLoading}
          onClick={() => void apply(!disabled)}
        >
          {disabled ? t(keys.admin.storeTagManagerAllow) : t(keys.admin.storeTagManagerDisable)}
        </Button>
      </Actions>
    </SectionCard>
  );
}

/** ISO timestamp → the short date these lines print. */
function formatDate(iso: string): string {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toISOString().slice(0, 10);
}
